import CompartmentElement from '@/components/purchase/checkout/seat-selection/modal/canvas/CompartmentElement';
import NonInteractiveElement from '@/components/purchase/checkout/seat-selection/modal/canvas/NonInteractiveElement';
import PlaceElement from '@/components/purchase/checkout/seat-selection/modal/canvas/PlaceElement';
import type {
  CompartmentDTO,
  FloorPlanIconsDTO,
  PlaceDTO,
} from '@/types/dto/agent';
import type { Floor } from '@/types/seatSelection';
import {
  calculateNestedOffset,
  groupNestedElements,
} from '@/utils/seatSelection';
import type Konva from 'konva';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Layer, Stage } from 'react-konva';

interface VehicleLayoutCanvasProps {
  container: HTMLDivElement;
  currentFloor: Floor;
  floorPlanIcons: FloorPlanIconsDTO;
  selectedReservationId?: string;
  onSeatSelect?: (seatId: string) => void;
  onPassengerSeatClick?: (seatId: string, reservationId: string) => void;
  allPassengers?: Array<{
    firstName: string;
    lastName: string;
    reservationId?: string;
    index: number;
  }>;
}

const VehicleLayoutCanvas: React.FC<VehicleLayoutCanvasProps> = ({
  container,
  currentFloor,
  floorPlanIcons,
  selectedReservationId,
  onSeatSelect,
  onPassengerSeatClick,
  allPassengers,
}) => {
  const stageRef = useRef<Konva.Stage>(null);
  const [hoveredSeatId, setHoveredSeatId] = useState<string | undefined>(
    undefined
  );
  const floorSize = currentFloor?.size;

  useEffect(() => {
    if (stageRef.current) {
      stageRef.current.batchDraw();
    }
  }, []);

  const handleClick = useCallback(
    (element: PlaceDTO | CompartmentDTO) => {
      if (!element.id) return;

      if (
        element.reservationId &&
        element.reservationId !== selectedReservationId
      ) {
        onPassengerSeatClick?.(element.id, element.reservationId);
      } else {
        onSeatSelect?.(element.id);
      }
    },
    [onPassengerSeatClick, onSeatSelect, selectedReservationId]
  );

  if (!floorSize) {
    return null;
  }

  const width = floorSize.x ?? 800;
  const height = floorSize.y ?? 600;

  const nonInteractiveElements = [
    ...(currentFloor.internals ?? []),
    ...(currentFloor.signs ?? []),
  ];

  const places = currentFloor.places ?? [];
  const nestedElements = groupNestedElements(places);
  const regularPlaces = places.filter((place) => !place.nestedInElementId);

  return (
    <Stage
      width={width}
      height={height}
      container={container}
      ref={stageRef}
      style={{ display: 'block', position: 'absolute', top: 0, left: 0 }}
      touchAction="none"
      listening
    >
      <Layer>
        {nonInteractiveElements.map((element) => (
          <NonInteractiveElement
            key={element.id}
            element={element}
            floorPlanIcons={floorPlanIcons}
            canvasHeight={height}
          />
        ))}

        {(currentFloor.compartments ?? []).map((compartment) => (
          <CompartmentElement
            key={compartment.id}
            element={compartment}
            canvasHeight={height}
            onClick={() => handleClick(compartment)}
            hoveredSeatId={hoveredSeatId}
            selectedReservationId={selectedReservationId}
          />
        ))}

        {regularPlaces.map((place) => (
          <PlaceElement
            key={place.id}
            element={place}
            floorPlanIcons={floorPlanIcons}
            canvasHeight={height}
            selectedReservationId={selectedReservationId}
            hoveredSeatId={hoveredSeatId}
            onClick={() => handleClick(place)}
            allPassengers={allPassengers}
            onMouseEnter={() => setHoveredSeatId(place.id)}
            onMouseLeave={() => setHoveredSeatId(undefined)}
          />
        ))}

        {nestedElements.map((group) => {
          const parentElement = group[0];

          return (
            <Fragment key={parentElement.id}>
              {[...group].reverse().map((element, i) => {
                const { xOffset, yOffset } = calculateNestedOffset(
                  parentElement.orientation?.id,
                  i,
                  group.length,
                  25
                );

                return (
                  <PlaceElement
                    key={`${parentElement.id}-${element.id}`}
                    element={{
                      ...element,
                      coordinates: {
                        x: (parentElement.coordinates?.x || 0) + xOffset,
                        y: (parentElement.coordinates?.y || 0) + yOffset,
                      },
                    }}
                    floorPlanIcons={floorPlanIcons}
                    canvasHeight={height}
                    selectedReservationId={selectedReservationId}
                    hoveredSeatId={hoveredSeatId}
                    onClick={() => handleClick(element)}
                    allPassengers={allPassengers}
                    onMouseEnter={() => setHoveredSeatId(element.id)}
                    onMouseLeave={() => setHoveredSeatId(undefined)}
                  />
                );
              })}
            </Fragment>
          );
        })}
      </Layer>
    </Stage>
  );
};

export default VehicleLayoutCanvas;
